import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { getIntegrationIcon } from '@/integrations/integration-icons';

import { Callout } from './callout';
import { DialogHeader } from './dialog-header';
import {
  describeLocalPathFailure,
  type TrustedLocalPathSetup,
} from './local-path-setup';
import { ModalCard } from './modal-card';

import type { LocalPathSetupForm } from './use-local-path-setup';
import type { ComponentType } from 'react';

const FAILURE_ICON = <Icons.info className="size-4" />;

type PathsPresentation = {
  addLabel: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  pathLabel?: (path: string) => string;
};

const FILESYSTEM_PATHS_CONFIG: PathsPresentation = {
  addLabel: 'Add folder',
  description: 'These folders set where Littlebird can look.',
  icon: Icons.folder,
};

const OBSIDIAN_PATHS_CONFIG: PathsPresentation = {
  addLabel: 'Add vault',
  description: 'These vaults set where Littlebird can look.',
  icon: Icons.book,
  pathLabel: pathDisplayName,
};

function pathDisplayName(path: string): string {
  return path.split(/[/\\]/).filter(Boolean).at(-1) ?? path;
}

function LocalPathsList({
  paths,
  config,
  emptyText,
  chooseLabel,
  isConnecting,
  onAdd,
  onRemove,
}: {
  paths: readonly string[];
  config: PathsPresentation;
  emptyText: string;
  chooseLabel: string;
  isConnecting: boolean;
  onAdd: () => void;
  onRemove: (path: string) => void;
}) {
  return (
    <YStack className="gap-4">
      <p className="text-xs text-foreground-muted">{config.description}</p>
      {paths.length === 0 ? (
        <YStack className="items-center gap-3 rounded-lg border border-dashed border-border-strong px-4 py-8 text-center">
          <p className="text-foreground-muted">{emptyText}</p>
          <Button
            variant="outline"
            size="sm"
            disabled={isConnecting}
            onClick={onAdd}
          >
            {chooseLabel}
          </Button>
        </YStack>
      ) : (
        <YStack className="overflow-hidden rounded-lg border border-border bg-surface">
          <YStack className="py-1">
            {paths.map((path) => {
              const pathLabel = config.pathLabel?.(path) ?? path;
              const removeLabel = `Remove ${pathDisplayName(path)}`;

              return (
                <XStack
                  key={path}
                  className="items-center justify-between gap-3 px-4 py-2"
                >
                  <XStack className="min-w-0 flex-1 items-center gap-3">
                    <config.icon className="size-4 shrink-0 text-foreground-muted" />
                    <span
                      title={config.pathLabel ? path : undefined}
                      className="min-w-0 flex-1 truncate font-mono text-sm text-foreground"
                    >
                      {pathLabel}
                    </span>
                  </XStack>
                  <IconButton
                    icon={Icons.trash}
                    variant="ghost"
                    size="sm"
                    tooltip={removeLabel}
                    aria-label={removeLabel}
                    disabled={isConnecting}
                    onClick={() => onRemove(path)}
                  />
                </XStack>
              );
            })}
          </YStack>
          <Button
            variant="ghost"
            disabled={isConnecting}
            onClick={onAdd}
            className="h-auto w-full rounded-none border-t border-border px-4 py-3 text-left text-foreground-muted [&>span]:w-full [&>span]:justify-start"
          >
            <Icons.plus className="size-4" />
            {config.addLabel}
          </Button>
        </YStack>
      )}
    </YStack>
  );
}

type LocalPathSetupModalProps = {
  setup: TrustedLocalPathSetup;
  form: LocalPathSetupForm;
  onCancel: () => void;
};

export const LocalPathSetupModal = ({
  setup,
  form,
  onCancel,
}: LocalPathSetupModalProps): React.ReactElement => {
  const IntegrationIcon = getIntegrationIcon(setup.iconKey);
  const isConnecting = form.status === 'connecting';
  const primaryLabel = form.failure ? 'Try again' : 'Connect';
  const config =
    setup.id === 'filesystem' ? FILESYSTEM_PATHS_CONFIG : OBSIDIAN_PATHS_CONFIG;

  return (
    <ModalCard onClose={onCancel} closeDisabled={!form.canCancel}>
      <DialogHeader
        icon={<IntegrationIcon className="size-5" />}
        title={setup.title}
      />
      <YStack className="gap-4 p-6">
        <LocalPathsList
          paths={form.paths}
          config={config}
          emptyText={setup.emptyText}
          chooseLabel={setup.chooseLabel}
          isConnecting={isConnecting}
          onAdd={form.pickPath}
          onRemove={form.removePath}
        />
        {form.failure ? (
          <Callout tone="danger" icon={FAILURE_ICON}>
            {describeLocalPathFailure(setup, form.failure)}
          </Callout>
        ) : null}
      </YStack>
      <XStack className="items-center justify-end gap-2 border-t border-border p-4 px-6">
        <Button
          variant="ghost"
          size="sm"
          disabled={!form.canCancel}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!form.canConnect}
          isLoading={isConnecting}
          loadingText="Connecting…"
          onClick={form.connect}
        >
          {primaryLabel}
        </Button>
      </XStack>
    </ModalCard>
  );
};
